// Libraries
import { useContext, useEffect, useState, lazy } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router';

// Dependencies
import { SERVER_ERROR } from 'hocs/components/ErrorComponent/constants';
import MemberModel from 'models/Member';
import MemberApplication from 'models/MemberApplication';
import TranslationsContext from 'providers/translationsProvider';
import {
  getMaintenanceNotificationTimeout,
  getMaintenanceRedirectTimeout,
  isMaintenanceComingSoon,
  isMaintenanceModeEnabled,
} from 'utils/maintenance';
import { getAuthLinkSearch } from 'utils/signupCampaign';
import queryString from 'utils/queryString';
import {
  ADMIN_DASHBOARD,
  APPLICATION,
  COHORT_REDIRECT_BASE,
  COHORT_REDIRECT_PROGRAM_SETUP,
  COMMUNITY,
  EXECUTIVE_SUMMARY,
  LOGIN,
  MANAGEMENT_PANEL_BASE,
  MANAGER_INVITE,
  MENTOR_MATCHING,
  ENTREPRENEUR_MATCHING,
  ORGANIZATION_DASHBOARD,
  ORGANIZATION_DASHBOARD_MY_COHORTS,
  ORGANIZATION_DASHBOARD_PROGRAM_GROUPS,
  ORGANIZATION_DASHBOARD_MANAGERS,
  PROFILE,
  RESET_PASSWORD,
  SETTINGS,
  SETTINGS_TAB,
  SIGNUP,
  TOOLS,
  VERIFY_EMAIL,
  VERIFY_EMAIL_TOKEN,
  DASHBOARD,
  VENTURE,
  TROUBLESHOOT,
  MAINTENANCE,
  VERIFY_EMAIL_CHANGE_TOKEN,
  SETTINGS_TABS,
} from './paths';
import withSuspense from 'hocs/withSuspense';
import useManager from 'features/manager/presentation/useManager';

// Actions & Selectors
import { selectors as memberSelectors } from 'store/modules/member';
import { selectors as mentorSelectors } from 'store/modules/mentor';
import { programLinkRequested } from 'store/modules/customProgramLink/customProgramLinkSlice';
import { actions as programActions } from 'store/modules/program';
import { selectors as projectSelectors } from 'store/modules/project';
import { actions as authActions, selectors as userSelectors } from 'store/modules/user';

// Services
import teamService from 'services/team';
import { useGetUserStatusQuery } from 'services/apiSlices/userApi';
import { canStartSession } from 'services/apiSlices/userStatus';

// Components
import { message, notification } from 'antd';
import ApiMessageHandler from 'components/ApiMessageHandler';
import WelcomePopup from 'components/WelcomePopup';
import ManagerRoute from 'containers/Routes/ManagerRoute';
import PrivateRoute from 'containers/Routes/PrivateRoute';
import ResetPasswordLayout from 'views/ResetPassword/Layout';
import ErrorComponent from 'hocs/components/ErrorComponent';
import ErrorModal from 'components/elements/Modal/ErrorModal';
import SkipToMainContent from 'components/elements/SkipToMainContent';
import CurrentPasswordModal from '../Settings/CurrentPasswordModal';

const STATUS_INVITE_ACCEPTED = 1;

export const NEW_DASHBOARD_URL = '/';

const Application = withSuspense(lazy(() => import('views/Application')));
const CohortRedirect = withSuspense(lazy(() => import('views/OrganizationDashboard/CohortRedirect')));
const Community = withSuspense(lazy(() => import('views/Community')));
const Dashboard = withSuspense(lazy(() => import('views/Dashboard')));
const ExecutiveSummary = withSuspense(lazy(() => import('views/ExecutiveSummary')));
const Login = withSuspense(lazy(() => import('views/Auth/Login')));
const ManagerInviteSignup = withSuspense(lazy(() => import('views/Auth/ManagerInviteSignup')));
const MentorMatching = withSuspense(lazy(() => import('views/Matching/MentorMatching')));
const MyProgram = withSuspense(lazy(() => import('views/Matching/MemberMatching')));
const MyVenture = withSuspense(lazy(() => import('views/MyVenture')));
const OrganizationDashboard = withSuspense(lazy(() => import('views/OrganizationDashboard')));
const OrganizationDashboardRedirect = withSuspense(
  lazy(() => import('views/OrganizationDashboard/RedirectToDefaultOrganization'))
);
const Profile = withSuspense(lazy(() => import('views/Profile')));
const ProgramManagement = withSuspense(lazy(() => import('views/PMAdmin')));
const ResetPassword = withSuspense(lazy(() => import('views/ResetPassword')));
const Settings = withSuspense(lazy(() => import('views/Settings')));
const Signup = withSuspense(lazy(() => import('views/Auth/Signup')));
const SuperAdmin = withSuspense(lazy(() => import('views/SuperAdmin')));
const Tools = withSuspense(lazy(() => import('views/Tools')));
const Troubleshoot = withSuspense(lazy(() => import('views/Troubleshoot')));
const VerifyEmail = withSuspense(lazy(() => import('views/Auth/VerifyEmail')));

// worker function to check if user can access tools
const checkToolAccess = (user, projects, t, pathname, canBypassRestrictions) => {
  const isMentor = user.isMentor();

  if (pathname.includes('/tools') && !isMentor) {
    message.destroy(); // only display one message

    const projectId = pathname && pathname.match(/\d/g).join('');
    const venture = projects && projects.size > 0 ? projects.get(String(projectId)) : null;
    const ventureOwner = venture ? venture.owner : null;

    switch (true) {
      // COMMENT THIS CASE TO TEST THIS FEATURE
      case window.dev:
        return true;
      case canBypassRestrictions:
        return true;
      case ventureOwner && !isMentor && ventureOwner.status === MemberModel.STATUS_PAYMENT_FAILED:
        message.error(
          t(
            'Your next subscription payment is overdue. Please update your payment method in order to access the Tools again.'
          )
        );
        return false;
      case ventureOwner && !isMentor && ventureOwner.status !== MemberModel.STATUS_INCUBATING:
        message.error(
          t(
            'You don’t have access to this tool yet. If you believe you should, please contact us via the support chat.'
          )
        );
        return false;
      default:
        return true;
    }
  }
  return true;
};

const AppRoutes = props => {
  const { t } = useContext(TranslationsContext);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { isManager, canBypassRestrictions } = useManager();

  const { organization, program } = queryString.parse(document.location.search);
  const isCustomLink = Boolean(organization && program);
  const isMaintenanceMode = isMaintenanceModeEnabled();

  const [toolAccess, setToolAccess] = useState(true);
  const [wasRedirected, setWasRedirected] = useState(window.location.pathname !== '/');
  const [acceptingInvite, setAcceptingInvite] = useState(false);

  // Selectors
  const user = useSelector(userSelectors.getUser);
  const isError = useSelector(userSelectors.isError);
  const mentor = useSelector(mentorSelectors.getMentor);
  const member = useSelector(memberSelectors.getMember);
  const isAuthenticated = useSelector(userSelectors.isAuthenticated);
  const projects = useSelector(projectSelectors.getProjectsById);
  const userStatusQuery = useGetUserStatusQuery(null, {
    skip: !isAuthenticated,
  });
  const { data: userData, refetch: refetcUserData } = userStatusQuery;

  // Actions
  const { listPrograms } = programActions;
  const { listPublicPrograms } = programActions;
  const { clearError } = authActions;

  const redirectOnLogin = () => {
    setWasRedirected(true);

    const ventureSummaryCompleted = member.application_venture_summary_complete;

    if (mentor.id && isManager) {
      // If the user is an Admin
      navigate(ORGANIZATION_DASHBOARD);
    } else if (mentor.id || member.id) {
      // If the user is a Mentor or Entrepreneur
      switch (true) {
        // (Latest E Application) Status:
        //
        // Application Incomplete: application page 0
        case member.application_status === MemberApplication.STATUS_IN_PROGRESS:
          navigate(`/program/${member.application_program}/application`);
          break;
        // Application Pending: dashboard
        case member.application_status === MemberApplication.STATUS_PENDING_DECISION:
          navigate('/');
          break;
        // Unmatched: Mentor Matching
        case member.application_status === MemberApplication.STATUS_UNMATCHED:
          navigate(`/program/${member.application_program}/matching`);
          break;
        // Incubating: Tools dashboard
        case member.application_status === MemberApplication.STATUS_INCUBATING &&
          ventureSummaryCompleted:
          if (window.location.pathname.indexOf('tools') === -1) {
            // If user is not in workspace already
            navigate(`/venture/${member.application_project}/workspace`);
          }
          break;
        case member.application_status === MemberApplication.STATUS_INCUBATING &&
          !ventureSummaryCompleted:
          navigate(`/venture/${member.application_project}/venture-summary`);
          break;
        default:
          navigate(NEW_DASHBOARD_URL);
          break;
      }
    }
  };

  const initialize = () => {
    if (isAuthenticated && canStartSession(userStatusQuery) && !wasRedirected) {
      dispatch(listPrograms());
      dispatch(listPublicPrograms());
      redirectOnLogin();
    }
  };

  // TODO: this should be moved down into the tools route
  useEffect(() => {
    if (user && projects) {
      setToolAccess(checkToolAccess(user, projects, t, location.pathname, canBypassRestrictions));
    }
  }, [props]);

  useEffect(() => {
    if (isAuthenticated && userData?.reset_password) navigate(NEW_DASHBOARD_URL);
    message.config({ duration: 10, maxCount: 1, top: 70 });
    if (member.id || (mentor.id && !isManager)) {
      initialize();
    }
    if (isMaintenanceComingSoon()) {
      setTimeout(
        () =>
          notification.warning({
            description: t('Please, make sure to save all your work and come back later.'),
            duration: 0,
            message: t('Scheduled maintenance starting soon'),
          }),
        getMaintenanceNotificationTimeout()
      );
    }
    const maintenanceRedirectTimeout = getMaintenanceRedirectTimeout();
    if (maintenanceRedirectTimeout && maintenanceRedirectTimeout > 0) {
      setTimeout(() => navigate('/maintenance'), maintenanceRedirectTimeout);
    }
  }, []);

  const acceptInvite = (memberToken, mentorToken) => {
    setAcceptingInvite(true);

    teamService.updateMemberInvite({
      id: memberToken || mentorToken,
      member: member && member.id,
      mentor: mentor && mentor.id,
      status: STATUS_INVITE_ACCEPTED,
    });
  };

  const { memberToken, mentorToken } = queryString.parse(document.location.search);
  if (isAuthenticated && !acceptingInvite && (memberToken || mentorToken) && (member || mentor)) {
    acceptInvite(memberToken, mentorToken);
  }
  if (member.id || mentor.id) {
    initialize();
  } else if (!isAuthenticated && wasRedirected) {
    setWasRedirected(false);
  }

  const isResetPassword = window.location.pathname.includes(RESET_PASSWORD);
  /**
   * Setting up the `rootAddress` dynamically depending on the user and its mentor status.
   */
  const rootAddress = isAuthenticated && isManager ? ORGANIZATION_DASHBOARD : '/';

  if (isMaintenanceMode && !window.location.pathname.includes('maintenance')) {
    navigate('/maintenance');
  }

  // AppRoutes runs too early (right after login, before the dashboard's data has loaded) to
  // reliably know whether the user is already applying to this program. Just record which
  // program the custom link asked for; ProgramsDashboardContainer already has the
  // fully-loaded data (programBlockingApplication) needed to decide whether that's a real
  // mismatch, so that's the only place that should make this decision.
  useEffect(() => {
    if (isCustomLink && isAuthenticated) {
      dispatch(programLinkRequested(Number(program)));
    }
  }, [dispatch, isCustomLink, isAuthenticated, program]);

  const handleModalClose = () => {
    dispatch(clearError());
  };

  return (
    <>
      {/*
        WelcomePopup is currently only for members. If we add more popups,
        add it WITHIN the component, not in Routes.js.
      */}
      {user.member && <WelcomePopup />}
      <ApiMessageHandler />
      {/*
        Open modal to force user to change default password
      */}
      {isAuthenticated && (
        <>
          {/* The login background behind the window to change a temporary password */}
          {userData?.reset_password && <ResetPasswordLayout />}
          <CurrentPasswordModal
            closeIcon={false}
            open={userData?.reset_password}
            onClose={() => {}}
            handleRefreshUserData={refetcUserData}
          />
          <ErrorModal
            onCancel={handleModalClose}
            visible={isError}
            title={t('Oops!')}
            description={t('The credentials are incorrect.')}
            buttonValue={t('TRY AGAIN')}
          />
        </>
      )}
      {isAuthenticated && <SkipToMainContent />}
      <div>
        {isMaintenanceMode ? (
          <Routes data-test="maintenance-Routes">
            <Route path="*" element={<ErrorComponent errorType={SERVER_ERROR} />} />
          </Routes>
        ) : (
          !userData?.reset_password && (
            <Routes>
              <Route
                path={LOGIN}
                element={
                  isAuthenticated ? (
                    <Navigate to={rootAddress} />
                  ) : (
                    <Login isCustomLink={isCustomLink} />
                  )
                }
              />
              <Route
                path={SIGNUP}
                element={
                  isAuthenticated ? (
                    <Navigate to={rootAddress} />
                  ) : (
                    <Signup isCustomLink={isCustomLink} />
                  )
                }
              />
              {/* Open to signed-in users too: existing managers accept their invite here */}
              <Route path={MANAGER_INVITE} element={<ManagerInviteSignup />} />
              <Route path={RESET_PASSWORD} element={<ResetPassword />} />

              {/* 
              PrivateRoute opening tag.
              PrivateRoute checks if should redirects user to /login or to /verify-email.
            */}
              <Route path="/" element={<PrivateRoute />}>
                <Route
                  path=""
                  element={
                    location.pathname !== rootAddress && !isResetPassword ? (
                      <Navigate to={rootAddress} />
                    ) : (
                      <Dashboard />
                    )
                  }
                />
                {/* ADMIN */}
                <Route element={<ManagerRoute />}>
                  <Route path={ADMIN_DASHBOARD} element={<SuperAdmin />} />
                  <Route path={ORGANIZATION_DASHBOARD} element={<OrganizationDashboardRedirect />} />
                  <Route
                    path={ORGANIZATION_DASHBOARD_MY_COHORTS}
                    element={<OrganizationDashboard page="my-cohorts" />}
                  />
                  <Route
                    path={ORGANIZATION_DASHBOARD_PROGRAM_GROUPS}
                    element={<OrganizationDashboard page="program-groups" />}
                  />
                  <Route
                    path={ORGANIZATION_DASHBOARD_MANAGERS}
                    element={<OrganizationDashboard page="organization-managers" />}
                  />
                  <Route path={`${MANAGEMENT_PANEL_BASE}/*`} element={<ProgramManagement />} />
                  <Route path={COHORT_REDIRECT_PROGRAM_SETUP} element={<CohortRedirect />} />
                  <Route path={COHORT_REDIRECT_BASE} element={<CohortRedirect />} />
                </Route>

                {/* COMMON */}
                <Route path={PROFILE} element={<Profile />} />
                <Route
                  path={SETTINGS}
                  element={
                    <Navigate
                      to={generatePath(SETTINGS_TAB, { tab: SETTINGS_TABS.general })}
                      replace
                    />
                  }
                />
                <Route path={SETTINGS_TAB} element={<Settings />} />

                {/* Es & Ms */}
                <Route path={DASHBOARD} element={<Dashboard programDetail />} />
                <Route path={APPLICATION} element={<Application />} />
                <Route path={ENTREPRENEUR_MATCHING} element={<MyProgram />} />
                <Route path={MENTOR_MATCHING} element={<MentorMatching />} />
                <Route path={TOOLS} element={toolAccess && <Tools />} />
                <Route path={VENTURE} element={<MyVenture />} />
                <Route path={EXECUTIVE_SUMMARY} element={<ExecutiveSummary />} />
                <Route path={`${COMMUNITY}/*`} element={<Community />} />

                {/* OTHERS */}
                <Route path={TROUBLESHOOT} element={<Troubleshoot />} />
                <Route path={MAINTENANCE} element={<ErrorComponent errorType={SERVER_ERROR} />} />
              </Route>
              {/* PrivateRoute closing tag */}

              {/* EMAIL VERIFICATION */}
              <Route path={VERIFY_EMAIL} element={<VerifyEmail />} />
              <Route path={VERIFY_EMAIL_TOKEN} element={<VerifyEmail />} />
              <Route path={VERIFY_EMAIL_CHANGE_TOKEN} element={<VerifyEmail isEmailChange />} />

              <Route
                path="*"
                element={
                  <Navigate to={{ pathname: LOGIN, search: getAuthLinkSearch(location.search) }} />
                }
              />
            </Routes>
          )
        )}
      </div>
    </>
  );
};

export default AppRoutes;
